/**
 * MetricChart - Modular Chart Component
 *
 * Flexible, reusable chart component supporting multiple chart types
 * with consistent configuration and styling
 */

import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import { Line, Bar, Doughnut, Pie } from 'react-chartjs-2';
import { TrendingUp } from 'lucide-react';
import { MetricChartConfig, GoalChartConfig } from './ChartTypes';
import {
  generateChartJSData,
  generateChartJSOptions,
  buildGoalAnnotations,
  buildGoalTooltip
} from './chartUtils';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  annotationPlugin
);

interface MetricChartProps {
  config: MetricChartConfig | GoalChartConfig;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Main MetricChart component
 */
export const MetricChart: React.FC<MetricChartProps> = ({ config, className, style }) => {
  // If it's a goal chart, enhance with goal-specific features
  if ('goalType' in config) {
    const goalConfig = config as GoalChartConfig;

    // Build goal annotations if not provided
    if (!goalConfig.annotations) {
      goalConfig.annotations = buildGoalAnnotations(goalConfig);
    }

    // Build goal tooltip if not provided
    if (!goalConfig.customTooltip) {
      goalConfig.customTooltip = buildGoalTooltip(goalConfig);
    }

    // Set axis labels if not provided
    if (!goalConfig.yAxisLabel) {
      goalConfig.yAxisLabel = goalConfig.unit;
    }
    if (!goalConfig.xAxisLabel) {
      goalConfig.xAxisLabel = 'Date';
    }
  }

  // Handle loading state
  if (config.loading) {
    return (
      <div className={`metric-chart-container ${className || ''}`} style={style}>
        <div className="metric-chart-loading">
          <TrendingUp className="w-12 h-12 animate-spin text-indigo-500" />
          <p>Loading chart data...</p>
        </div>
        <style>{chartStyles}</style>
      </div>
    );
  }

  // Handle error state
  if (config.error) {
    return (
      <div className={`metric-chart-container ${className || ''}`} style={style}>
        <div className="metric-chart-error">
          <h3>Error Loading Chart</h3>
          <p>{config.error}</p>
        </div>
        <style>{chartStyles}</style>
      </div>
    );
  }

  // Handle empty state
  if (config.datasets.length === 0 || config.datasets[0].data.length === 0) {
    return (
      <div className={`metric-chart-container ${className || ''}`} style={style}>
        <div className="metric-chart-empty">
          <TrendingUp className="w-12 h-12 text-gray-400" />
          <p>{config.emptyMessage || 'No data available'}</p>
        </div>
        <style>{chartStyles}</style>
      </div>
    );
  }

  // Generate Chart.js data and options
  const chartData = generateChartJSData(config);
  const chartOptions = generateChartJSOptions(config);

  // Handle chart click — use intersect:true to distinguish point clicks from background clicks
  const handleClick = (config.onPointClick || config.onBackgroundClick)
    ? (event: any, _elements: any[], chart: any) => {
        // Re-query with intersect:true so only direct point hits count
        const hitElements = chart.getElementsAtEventForMode(
          event, 'nearest', { intersect: true }, false
        );
        if (hitElements.length === 0) {
          config.onBackgroundClick?.();
          return;
        }
        if (config.onPointClick) {
          const el = hitElements[0];
          const dsIndex = el.datasetIndex;
          const ptIndex = el.index;
          const dataset = config.datasets[dsIndex];
          const dataPoint = dataset?.data[ptIndex];
          if (dataPoint) {
            config.onPointClick(dataPoint, dsIndex, ptIndex);
          }
        }
      }
    : undefined;

  // Render appropriate chart type
  const renderChart = () => {
    const chartProps: any = {
      data: chartData,
      options: chartOptions,
      height: config.height || 400,
    };

    if (handleClick) {
      chartProps.options = {
        ...chartProps.options,
        onClick: handleClick,
      };
    }

    switch (config.type) {
      case 'line':
      case 'area':
        return <Line {...chartProps} />;
      case 'bar':
        return <Bar {...chartProps} />;
      case 'doughnut':
        return <Doughnut {...chartProps} />;
      case 'pie':
        return <Pie {...chartProps} />;
      default:
        return <Line {...chartProps} />;
    }
  };

  return (
    <div
      className={`metric-chart-container ${className || ''}`}
      style={{
        width: '100%',
        height: config.height || 400,
        ...style
      }}
    >
      {renderChart()}
      <style>{chartStyles}</style>
    </div>
  );
};

/**
 * Component styles
 */
const chartStyles = `
  .metric-chart-container {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .metric-chart-loading,
  .metric-chart-error,
  .metric-chart-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    gap: 16px;
    color: var(--text-secondary);
  }

  .metric-chart-error h3 {
    color: var(--text-primary);
    margin: 0;
  }

  .metric-chart-error p,
  .metric-chart-empty p,
  .metric-chart-loading p {
    color: var(--text-secondary);
    margin: 0;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  .animate-spin {
    animation: spin 1s linear infinite;
  }
`;

export default MetricChart;
